import * as qs from "qs";
import { StepId } from "~/applications/Checkout/Ui/CheckoutProcessWizardForm";
import CheckoutPublicState from "~/applications/Checkout/Ui/CheckoutPublicState";
import { OpeningResult } from "~/applications/OpenAccess/Domain/OpeningResult";
import { OpeningTechnology } from "~/applications/OpenAccess/Domain/OpeningTechnology";
import StayRequest from "~/core/StayRequest";

export type Route = string;

export enum Referrer {
  CHECKOUT = "checkout",
}

const recordToQueryString = (data: Record<string, unknown>) =>
  qs.stringify(data, { encodeValuesOnly: true, arrayFormat: "repeat" });

export const homepageRoute = (): Route => {
  return "/";
};

export const identityCheckPageRoute = (reservationId: string): Route =>
  `/identity-check/${reservationId}`;

export const offersRoute = (
  destinationSlug: string,
  stayRequest: StayRequest | undefined,
  hotelIds?: Array<string> | undefined,
): Route => {
  const stayRequestQueryString = stayRequest
    ? "?" + recordToQueryString({ ...stayRequest.toJSON() })
    : "";

  const hotelIdQueryString =
    hotelIds !== undefined ? `&logeIds=${hotelIds?.join(",")}` : "";

  return `/destinations/${destinationSlug}/offers${stayRequestQueryString}${hotelIdQueryString}`;
};

export const hotelPageRoute = (
  destinationSlug: string,
  hotelSlug: string,
  stayRequest: StayRequest | undefined,
): Route => {
  const stayRequestQueryString = stayRequest
    ? "?" + recordToQueryString({ ...stayRequest.toJSON() })
    : "";

  return `/destinations/${destinationSlug}/${hotelSlug}${stayRequestQueryString}`;
};

export const offerSearchUnitGroupRoute = (
  destinationSlug: string,
  hotelSlug: string,
  unitGroupSlug: string,
  stayRequest: StayRequest | undefined,
): Route => {
  const stayRequestQueryString = stayRequest
    ? "?" + recordToQueryString({ ...stayRequest.toJSON() })
    : "";

  return `/destinations/${destinationSlug}/${hotelSlug}/${unitGroupSlug}${stayRequestQueryString}`;
};

export const newBookingEngineSessionRoute = (
  stayRequest: StayRequest,
  hotelId: string,
  unitGroupId: string,
): Route => {
  return `/checkout/new?${recordToQueryString({
    ...stayRequest.toJSON(),
    logeId: hotelId,
    unitGroupId,
    travelPurpose: stayRequest.travelPurpose,
  })}`;
};

export const bookingEngineRoute = (
  stepId: StepId,
  checkoutPublicState: CheckoutPublicState,
): Route => {
  return `/checkout/step/${stepId}?${recordToQueryString(
    checkoutPublicState.toJSON(),
  )}`;
};

export const reservationConfirmedRoute = (
  reservationId: string,
  referrer: Referrer | null = null,
): Route =>
  `/reservation/${reservationId}?${recordToQueryString({ referrer })}`;

export const gogailleJourneyRoute = (): Route => "/me/journey";

export const gogailleJourneyDetailsRoute = (reservationId: string): Route =>
  `/me/journey/${reservationId}`;

export const gogailleProfileRoute = (): Route => "/me/profile";

export const gogailleHelpRoute = (): Route => "/me/help";

export const gogailleSavedGuestRoute = (): Route => "/me/profile/saved-guests";

export const gogailleBillingAddressesRoute = (): Route =>
  "/me/profile/billing-addresses";

export const gogaillePreferencesRoute = (): Route => "/me/profile/preferences";

export const gogailleOpenAccessRoute = (
  accessId: string,
  technology: OpeningTechnology,
  openingResult?: OpeningResult,
): Route =>
  `/open-access/${accessId}?${recordToQueryString({
    technology,
    openingResult,
  })}`;

export const gogailleTermsGeneralConditionsRoute = () =>
  "/terms/general-conditions";

export const gogailleLegalMentionsRoute = () => "/legal/mentions";

export const gogaillePrivacyPolicyRoute = () => "/privacy/policy";

export const gogaillePrivacyDataProtectionRoute = () =>
  "/privacy/data-protection";

export const gogaillePrivacyCookiesRoute = () => "/privacy/cookies";

export const guestAppStayPageRoute = (reservationId: string) =>
  `${process.env.PUBLIC_URL}/app/stay/${reservationId}`;

export const guestAppIdentityCheckPageRoute = (reservationId: string) =>
  `${process.env.PUBLIC_URL}/app/stay/${reservationId}/identity-check`;
